import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import type { FlightsClassTypes, FareClassWithReward } from 'constants/flight'
import type { MaldivesCabinHotelEntry } from '../hooks/useMaldivesOffersPricing'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import {
  getLogicalScrollLeft,
  scrollToLogicalPosition,
} from 'LuxKit/Carousel/utils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import zIndex from 'styles/tools/z-index'
import MaldivesCharterFareCarouselOption, { type MaldivesCharterTripOption } from './MaldivesCharterFareCarouselOption'
import { orderMaldivesCharterFareClassesFullPresentation } from 'lib/maldivesCharter/charterCabins'

/** Ignore tiny/sub-pixel scroll offsets before showing the blocking fare-swap transition. */
const FARE_SWAP_SCROLL_START_THRESHOLD_PX = 16
/** Long enough to cover the smooth scroll-to-start animation without leaving the carousel locked. */
const FARE_SWAP_TRANSITION_MS = 340

const CarouselWrap = styled.div`
  grid-column: 1 / -1;
  padding-block: ${rem(12)} ${rem(8)};
  padding-inline: 0;
  background-color: #fff;
`

const FareCarouselShell = styled.div<{ $dimmed: boolean }>`
  position: relative;
  opacity: ${p => (p.$dimmed ? 0.48 : 1)};
  transition: opacity 0.2s ease;
  ${p => (p.$dimmed ? 'pointer-events: none;' : '')}
`

const FareCarouselLoadingStatus = styled.div`
  position: absolute;
  inset: 0;
  z-index: ${zIndex.min};
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: ${p => p.theme.palette.neutral.default.two};
`

const FareCarouselLoadingIndicator = styled(LoadingIndicator)`
  padding: 0;
`

interface Props {
  fareClasses: ReadonlyArray<FareClassWithReward>
  selectedFareClass: FareClassWithReward
  /** When true, the matching fare card shows the chosen "Selected" treatment for this calendar date row. */
  isDateRowSelected: boolean
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  oneWayBundle?: boolean
  tripOption: MaldivesCharterTripOption
  onPickFare: (fareClass: FareClassWithReward, option: MaldivesCharterTripOption) => void
  onUnselectSelectedFare?: () => void
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
}

function MaldivesCharterDateFareCarousel(props: Props) {
  const {
    fareClasses,
    selectedFareClass,
    isDateRowSelected,
    checkIn,
    checkOut,
    nights,
    rooms,
    priceMode,
    luxPlusEnabled,
    oneWayBundle,
    tripOption,
    onPickFare,
    onUnselectSelectedFare,
    hotelPriceByCabinAndDate,
  } = props

  const intl = useIntl()
  const carouselRef = useRef<HTMLDivElement>(null)
  const previousSelectedFareRef = useRef<FareClassWithReward | null>(null)
  const [showFareSwapTransition, setShowFareSwapTransition] = useState(false)
  const [fareDetailsExpanded, setFareDetailsExpanded] = useState(false)
  const [extraFeaturesExpanded, setExtraFeaturesExpanded] = useState(false)

  const toggleFareDetails = useCallback(() => {
    setFareDetailsExpanded(v => !v)
  }, [])

  const toggleExtraFeatures = useCallback(() => {
    setExtraFeaturesExpanded(v => !v)
  }, [])

  const fareSwapSpinnerAriaLabel = intl.formatMessage({
    id: '8majp7K16JIT9Rfaq5Nb',
    defaultMessage: 'Updating fare options',
  })

  const orderedFareClasses = useMemo(() => {
    return orderMaldivesCharterFareClassesFullPresentation(fareClasses)
  }, [fareClasses])

  useLayoutEffect(() => {
    if (previousSelectedFareRef.current === null) {
      previousSelectedFareRef.current = selectedFareClass
      return
    }
    if (previousSelectedFareRef.current === selectedFareClass) {
      return
    }
    previousSelectedFareRef.current = selectedFareClass

    const el = carouselRef.current
    if (!el) {
      return
    }

    const hadScrolledAwayFromStart = getLogicalScrollLeft(el) > FARE_SWAP_SCROLL_START_THRESHOLD_PX
    if (hadScrolledAwayFromStart) {
      setShowFareSwapTransition(true)
    }

    scrollToLogicalPosition(el, {
      left: 0,
      behavior: hadScrolledAwayFromStart ? 'smooth' : 'auto',
    })

    if (!hadScrolledAwayFromStart) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setShowFareSwapTransition(false)
    }, FARE_SWAP_TRANSITION_MS)

    return () => {
      window.clearTimeout(timeoutId)
      setShowFareSwapTransition(false)
    }
  }, [selectedFareClass])

  return (
    <CarouselWrap>
      <FareCarouselShell
        $dimmed={showFareSwapTransition}
        aria-busy={showFareSwapTransition}
      >
        {showFareSwapTransition && (
          <FareCarouselLoadingStatus role="status" aria-live="polite" aria-label={fareSwapSpinnerAriaLabel}>
            <FareCarouselLoadingIndicator inline ballSize={8} />
          </FareCarouselLoadingStatus>
        )}
        <CardCarousel
          ref={carouselRef}
          gap={12}
          snap="start"
          gutterSize={16}
          gutterStyle="overflow"
        >
          {orderedFareClasses.map((fc) => (
            <MaldivesCharterFareCarouselOption
              key={fc}
              optionFareClass={fc}
              checkIn={checkIn}
              checkOut={checkOut}
              nights={nights}
              rooms={rooms}
              priceMode={priceMode}
              luxPlusEnabled={luxPlusEnabled}
              oneWayBundle={oneWayBundle}
              isSelectedFare={isDateRowSelected && fc === selectedFareClass}
              tripOption={tripOption}
              onPickFare={onPickFare}
              onUnselectSelectedFare={onUnselectSelectedFare}
              fareDetailsExpanded={fareDetailsExpanded}
              extraFeaturesExpanded={extraFeaturesExpanded}
              onToggleFareDetails={toggleFareDetails}
              onToggleExtraFeatures={toggleExtraFeatures}
              hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
            />
          ))}
        </CardCarousel>
      </FareCarouselShell>
    </CarouselWrap>
  )
}

export default MaldivesCharterDateFareCarousel
